import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import {v4} from 'uuid';

import {
  HIGHLIGHT_POLICY_STEPID,
  HIGHLIGHT_POLICY_TITLE,
  HIGHLIGHT_PROCEDURE_DESCRIPTION_STEPID,
  HIGHLIGHT_PROCEDURE_STEP_TITLE_PREFIX,
  HIGHLIGHT_PROCEDURE_TITLE_STEPID,
  HIGHLIGHT_PROCESS_DESCRIPTION_STEPID,
  HIGHLIGHT_PROCESS_STEP_TITLE_PREFIX,
  HIGHLIGHT_PROCESS_TITLE_STEPID,
  lightenColor,
} from 'app/general/structures/UserContentContainer/utils/highlightUtils';
import {usePolicyVersionById} from 'app/policies/api';
import {useProcedureVersionById} from 'app/procedures/api';
import {useProcessVersionDetail} from 'app/process/api';
import {
  calculateOperations,
  htmlToTokens,
  type Token,
} from 'app/string/htmldiff';
import {useTaskInstance} from 'app/tasks/api';

import {HighlightAnimations} from './components/HighlightAnimations';

import type {InlineCommentVersionType, TextHighlightType} from './types';
import type {
  CommentableContentType,
  CommentHighlightParams,
  DetailPolicyVersionSerializedType,
  ProcedureContentStepType,
  ProcedureVersionSerializedType,
  ProcessVersionSerializedType,
} from 'sweet-types';
import type {ProcessContentStepType} from 'sweet-types/process/process';

interface TextHighlightsContextType {
  addHighlight: (
    conversationId: string | null,
    startParams: CommentHighlightParams,
    endParams: CommentHighlightParams,
    versionId: number,
    contentType: CommentableContentType,
    selectedText: string,
    stepId: string,
    color?: string
  ) => string;
  clearHighlight: (conversationId: string) => void;
  highlights: Record<string, TextHighlightType>;
  setCurrentVersionId: (newVersionId: number) => void;
  hoveredConversationId: string | null;
  setHoveredConversationId: React.Dispatch<React.SetStateAction<string | null>>;
  getTextFromTokens: (
    versionId: number,
    stepId: string,
    startToken: number,
    startTokenTextOffset: number,
    endToken: number,
    endTokenTextOffset: number
  ) => string | undefined;
}

export const TextHighlightsContext = createContext<
  TextHighlightsContextType | undefined
>(undefined);

export const useTokensForDocumentVersions = (
  contentType: 'policy' | 'procedure' | 'proceduregroup' | 'taskinstance'
) => {
  const [contentTokens, setContentTokens] = useState<
    Record<number, Record<string, Array<Token>>>
  >({});

  const [versionIdQueue, setVersionIdQueue] = useState<Array<number>>([]);

  const fetchHooks = {
    policy: (versionId: number | typeof skipToken) =>
      usePolicyVersionById(versionId === skipToken ? versionId : {versionId}),
    procedure: (versionId: number | typeof skipToken) =>
      useProcedureVersionById(
        versionId === skipToken ? versionId : {versionId}
      ),
    proceduregroup: (versionId: number | typeof skipToken) =>
      useProcessVersionDetail(
        versionId === skipToken ? versionId : {versionId}
      ),
    taskinstance: useTaskInstance,
  };

  const useFetchHook = fetchHooks[contentType];

  const {data} = useFetchHook(
    versionIdQueue.length > 0 ? versionIdQueue[0] : skipToken,
    {refetchOnMountOrArgChange: false}
  ) as {data: InlineCommentVersionType | undefined};

  function isPolicyVersion(
    data: InlineCommentVersionType
  ): data is DetailPolicyVersionSerializedType {
    return (
      contentType === 'policy' &&
      'content' in data &&
      typeof data.content === 'string'
    );
  }

  function isProcedureVersion(
    data: InlineCommentVersionType
  ): data is ProcedureVersionSerializedType {
    return (
      contentType === 'procedure' &&
      'content' in data &&
      Array.isArray(data.content)
    );
  }

  function isProcessVersion(
    data: InlineCommentVersionType
  ): data is ProcessVersionSerializedType {
    return (
      contentType === 'proceduregroup' &&
      'content' in data &&
      Array.isArray(data.content)
    );
  }

  useEffect(() => {
    if (data != null && contentTokens[data.id] == null) {
      let newTokens: Record<string, Array<Token>> = {};

      if (isPolicyVersion(data)) {
        newTokens[HIGHLIGHT_POLICY_TITLE] = htmlToTokens(data.name);
        newTokens[HIGHLIGHT_POLICY_STEPID] = htmlToTokens(data.content);
      } else if (isProcedureVersion(data)) {
        data.content.forEach((step: ProcedureContentStepType) => {
          newTokens[step.id] = htmlToTokens(step.content);
          newTokens[`${HIGHLIGHT_PROCEDURE_STEP_TITLE_PREFIX}-${step.id}`] =
            htmlToTokens(step.title);
        });
        // also store the description as 'blank step id'.
        newTokens[HIGHLIGHT_PROCEDURE_DESCRIPTION_STEPID] = htmlToTokens(
          data.description.content
        );
        newTokens[HIGHLIGHT_PROCEDURE_TITLE_STEPID] = htmlToTokens(data.name);
      } else if (isProcessVersion(data)) {
        data.content.forEach((step: ProcessContentStepType) => {
          if ('content' in step) {
            newTokens[step.id] = htmlToTokens(step.content);
          }
          newTokens[`${HIGHLIGHT_PROCESS_STEP_TITLE_PREFIX}-${step.id}`] =
            htmlToTokens(step.title || '');
        });
        newTokens[HIGHLIGHT_PROCESS_DESCRIPTION_STEPID] = htmlToTokens(
          data.description.content
        );
        newTokens[HIGHLIGHT_PROCESS_TITLE_STEPID] = htmlToTokens(data.name);
      }

      setContentTokens((prev) => ({
        ...prev,
        [data.id]: newTokens,
      }));

      setVersionIdQueue((prev) => prev.filter((id) => id !== data.id));
    }
  }, [data, contentType]);

  function addVersionIdToQueue(newId: number | null) {
    if (
      newId != null &&
      contentTokens[newId] == null &&
      versionIdQueue.indexOf(newId) === -1
    ) {
      setVersionIdQueue((prev) => prev.concat([newId]));
    }
  }
  const getVersionTokens = (versionId: number, stepId: string) => {
    if (
      contentTokens[versionId] != null &&
      contentTokens[versionId][stepId] != null
    ) {
      return contentTokens[versionId][stepId];
    } else {
      addVersionIdToQueue(versionId);
    }
    return;
  };

  return {
    getVersionTokens,
    versionIdQueue,
    addVersionIdToQueue,
  };
};

export const TextHighlightsContextProvider = ({
  children,
  contentType,
}: {
  children: ReactNode;
  contentType: 'policy' | 'procedure' | 'proceduregroup' | 'taskinstance';
}) => {
  const {getVersionTokens, versionIdQueue, addVersionIdToQueue} =
    useTokensForDocumentVersions(contentType);

  const [currentVersionId, setCurrentVersionId] = useState<number | null>(null);

  const [hoveredConversationId, setHoveredConversationId] = useState<
    string | null
  >(null);

  const [highlights, setHighlights] = useState<
    Record<string, TextHighlightType>
  >({});

  function syncHighlightToCurrentVersionId(highlight: TextHighlightType) {
    if (currentVersionId == null) {
      return null;
    }
    const highlightTokens = getVersionTokens(
      highlight.versionId,
      highlight.stepId
    );
    const currentVersionTokens = getVersionTokens(
      currentVersionId,
      highlight.stepId
    );

    if (highlightTokens == null || currentVersionTokens == null) {
      return null;
    }

    const operations = calculateOperations(
      highlightTokens,
      currentVersionTokens
    );

    let changedStartToken = highlight.startParams.token;
    let changedStartTokenOffset = highlight.startParams.tokenTextOffset;
    let changedEndToken = highlight.endParams.token;
    let changedEndTokenOffset = highlight.endParams.tokenTextOffset;

    const highlightStart = highlight.startParams.token;
    const highlightEnd = highlight.endParams.token;

    operations.forEach((operation) => {
      if (operation.action === 'delete' || operation.action === 'replace') {
        const operationStart = operation.startInBefore;
        const operationEnd = operation.endInBefore;

        if (operationStart <= highlightStart) {
          changedStartToken -=
            Math.min(highlightStart - 1, operationEnd) - operationStart + 1;
        }

        if (operationStart <= highlightEnd) {
          changedEndToken -=
            Math.min(highlightEnd, operationEnd) - operationStart + 1;
        }

        if (
          operationStart <= highlightStart &&
          operationEnd >= highlightStart
        ) {
          changedStartTokenOffset = 0;
        }

        if (operationStart <= highlightEnd && operationEnd >= highlightEnd) {
          changedEndTokenOffset =
            currentVersionTokens[changedEndToken]?.string.length;
        }
      }

      if (operation.action === 'insert' || operation.action === 'replace') {
        const operationStart = operation.startInAfter;

        const insertLength = operation.endInAfter - operation.startInAfter + 1;

        if (operationStart <= changedEndToken) {
          changedEndToken += insertLength;
        }
        if (operationStart <= changedStartToken) {
          changedStartToken += insertLength;
        }
      }
    });

    const changedHighlight: TextHighlightType = {
      ...highlight,
      startParams: {
        ...highlight.startParams,
        token: changedStartToken,
        tokenTextOffset: changedStartTokenOffset,
      },
      endParams: {
        ...highlight.endParams,
        token: changedEndToken,
        tokenTextOffset: changedEndTokenOffset,
      },
      versionId: currentVersionId,
    };

    return changedHighlight;
  }

  function updateHighlightsToDocumentVersion() {
    Object.values(highlights)
      .filter(
        (highlight) =>
          currentVersionId != null &&
          highlight.versionId !== currentVersionId &&
          getVersionTokens(highlight.versionId, highlight.stepId) != null
      )
      .map((highlight) => {
        const changedHighlight = syncHighlightToCurrentVersionId(highlight);
        if (changedHighlight != null) {
          setHighlights((prev) => {
            const newHighlights = {
              ...prev,
            };
            newHighlights[changedHighlight.id] = changedHighlight;
            return newHighlights;
          });
        }
      });
  }

  useEffect(() => {
    updateHighlightsToDocumentVersion();
  }, [versionIdQueue, currentVersionId, highlights]);

  const addHighlight = (
    id: string | null,
    startParams: CommentHighlightParams,
    endParams: CommentHighlightParams,
    versionId: number,
    contentType: CommentableContentType,
    selectedText: string,
    stepId: string,
    color = '#FFFF00'
  ) => {
    const newConversationId = id || v4();

    setHighlights((prev) => ({
      ...prev,
      [newConversationId]: {
        id: newConversationId,
        startParams,
        endParams,
        versionId,
        contentType,
        selectedText,
        stepId,
        color,
        colorLight: lightenColor(color, 0.5),
      },
    }));
    addVersionIdToQueue(versionId);
    return newConversationId;
  };

  useEffect(() => {
    addVersionIdToQueue(currentVersionId);
  }, [currentVersionId]);

  const clearHighlight = (highlightId: string) => {
    setHighlights((prev) => {
      const newHighlights = {...prev};
      delete newHighlights[highlightId];
      return newHighlights;
    });
  };

  const getTextFromTokens = (
    versionId: number,
    stepId: string,
    startToken: number,
    startTokenTextOffset: number,
    endToken: number,
    endTokenTextOffset: number
  ) => {
    const tokens = getVersionTokens(versionId, stepId);
    if (tokens == null) {
      return;
    }

    const selectedTokens = tokens
      .slice(startToken, endToken + 1)
      .map((token) => token.string);

    selectedTokens[0] = selectedTokens[0].substring(startTokenTextOffset);
    selectedTokens[selectedTokens.length - 1] = selectedTokens[
      selectedTokens.length - 1
    ].substring(0, endTokenTextOffset);

    return selectedTokens.join('');
  };

  return (
    <TextHighlightsContext.Provider
      value={{
        addHighlight,
        clearHighlight,
        highlights,
        setCurrentVersionId,
        hoveredConversationId,
        setHoveredConversationId,
        getTextFromTokens,
      }}
    >
      <HighlightAnimations>{children}</HighlightAnimations>
    </TextHighlightsContext.Provider>
  );
};

export const useTextHighlights = () => {
  const context = useContext(TextHighlightsContext);
  if (!context) {
    throw new Error(
      'useTextHighlights must be used within an TextHighlightsContextProvider'
    );
  }
  return context;
};
